<i18n lang="yaml" locale="en">
  remove: Remove
  attention: Attention!
  content will be removed: All contents of this box will be deleted. Continue?
</i18n>

<i18n lang="yaml" locale="de">
  remove: Entfernen
  attention: Achtung!
  content will be removed: Alle Inhalte dieser Box werden gelöscht. Fortfahren?
</i18n>

<template>
  <draggable-resizeable
    ref="vdr"
    :target="$parent"
    :active="active"
    :h="boxHeight"
    :w="boxWidth"
    :x="boxX"
    :y="boxY"
    :minw="$grid.boxWidth * 4"
    :minh="$grid.boxHeight * 4"
    :grid="[$grid.boxWidth, $grid.boxHeight]"
    :parent="true"
    @resizestart="onResizeStart"
    @resizestop="saveNewDimensions"
    @dragstart="onDragStart"
    @dragstop="saveNewPosition"
    drag-handle=".edit-bar"
  >
    <slot />
    <font-dropzone :target-props="['fontFront', 'fontBack']" />
    <transition name="fade">
      <div
        class="editor edit-bar"
        :class="{transparent: isTinyBox}"
        :style="{width: boxWidth - 20 + 'px'}"
        v-if="(mouseover || dragging) && !resizing"
        @pointerdown.stop=""
      >
        <template v-if="!isSmallBox">
          <settings
            size="xs"
            @click="togglePropEditor"
          />
          <copy
            v-if="!$localeMode"
            size="xs"
            @click="cloneBox"
          />
          <add-circle
            v-if="!$localeMode"
            size="xs"
            @click="toggleChildMenu"
            :active="showChildMenu"
          />
          <transition name="drop">
            <div
              class="child-menu"
              v-if="showChildMenu"
            >
              <div
                class="child-type"
                v-for="(title, type) in $parent.$options.childOptions"
                @click="setChild(type)"
                :key="type"
              >
                {{ $parent.$t(title) }}
              </div>
              <div
                v-if="hasActiveChildren"
                class="child-type remove-child"
                @click="setChild()"
              >
                {{ $t('remove') }}
              </div>
            </div>
          </transition>
          <edit
            v-if="hasActiveChildren"
            size="xs"
            @click="toggleChildPropEditor"
          />
          <flip
            size="xs"
            @click="flip()"
          />
        </template>
        <div
          v-if="!$localeMode || isSmallBox || isTinyBox"
          class="hamburger hamburger--minus"
          :class="{offset: isTinyBox, 'is-active': showMenu}"
          @click="toggleMenu"
        >
          <div class="hamburger-box">
            <div class="hamburger-inner" />
          </div>
        </div>
        <transition name="drop">
          <div
            class="menu-dropdown"
            :class="{offset: isTinyBox}"
            v-if="showMenu"
            :style="{top: 31 + 'px', left: (isTinyBox ? boxWidth + 2 : boxWidth - 62) + 'px'}"
          >
            <div
              v-if="!$localeMode"
              class="entry"
              @click="deleteBox"
            >
              <delete size="xs" />
            </div>
            <div
              v-if="!$localeMode"
              class="entry"
              @click="move(+Infinity)"
            >
              <first :rotate="90" />
            </div>
            <div
              v-if="!$localeMode"
              class="entry"
              @click="move(1)"
            >
              <chevron :rotate="-90" />
            </div>
            <div
              v-if="!$localeMode"
              class="entry"
              @click="move(-1)"
            >
              <chevron :rotate="90" />
            </div>
            <div
              v-if="!$localeMode"
              class="entry"
              @click="move(-Infinity)"
            >
              <first :rotate="-90" />
            </div>
            <template v-if="isSmallBox">
              <div
                class="entry"
                @click="togglePropEditor"
              >
                <settings size="s" />
              </div>
              <div
                v-if="!$localeMode"
                class="entry"
                @click="cloneBox"
              >
                <copy size="xs" />
              </div>
              <div
                class="entry"
                @click="toggleChildMenu"
              >
                <add-circle
                  size="s"
                  :active="showChildMenu"
                />
              </div>
              <transition name="drop">
                <div
                  class="child-menu offset"
                  v-if="showChildMenu && !$localeMode"
                >
                  <div
                    class="child-type"
                    v-for="(title, type) in $parent.$options.childOptions"
                    @click="setChild(type)"
                    :key="type"
                  >
                    {{ $parent.$t(title) }}
                  </div>
                  <div
                    v-if="hasActiveChildren"
                    class="child-type remove-child"
                    @click="setChild()"
                  >
                    {{ $t('remove') }}
                  </div>
                </div>
              </transition>
              <div
                v-if="hasActiveChildren"
                class="entry"
                @click="toggleChildPropEditor"
              >
                <edit size="s" />
              </div>
              <div
                class="entry"
                @click="flip()"
              >
                <flip
                  size="s"
                />
              </div>
            </template>
          </div>
        </transition>
      </div>
    </transition>
  </draggable-resizeable>
</template>

<script>
import DraggableResizeable from './draggable-resizeable'
import Settings from '@/components/icons/Settings'
import Edit from '@/components/icons/Edit'
import Copy from '@/components/icons/Copy'
import Delete from '@/components/icons/Delete'
import First from '@/components/icons/First'
import Chevron from '@/components/icons/Chevron'
import AddCircle from '@/components/icons/AddCircle'
import Flip from '@/components/icons/Flip'
import { Confirm } from '@/plugins/snotify'
import FontDropzone from '@/components/editor/FontDropzone'

export default {
  components: {
    DraggableResizeable,
    Settings,
    Edit,
    Copy,
    Delete,
    First,
    Chevron,
    AddCircle,
    Flip,
    FontDropzone
  },
  props: {
    // eslint-disable-next-line vue/prop-name-casing
    _slot: {
      type: String,
      default: 'front'
    }
  },
  inject: ['$grid'],
  data () {
    return {
      mouseover: false,
      showChildMenu: false,
      showMenu: false,
      dragging: false,
      resizing: false
    }
  },
  mounted () {
    this.$parent.$el.addEventListener('mouseover', this.onMouseOver)
    this.$parent.$el.addEventListener('mouseleave', this.onMouseLeave)
  },
  beforeDestroy () {
    this.$parent.$el.removeEventListener('mouseover', this.onMouseOver)
    this.$parent.$el.removeEventListener('mouseleave', this.onMouseLeave)
  },
  computed: {
    active () {
      return this.mouseover || this.dragging || this.resizing
    },
    hasChildren () {
      return this.$store.getters && this.$store.getters.numChildren > 0
    },
    hasActiveChildren () {
      // hasChildren, but only for the flipped slot
      return this.$store.getters && this.$store.getters.numChildrenBySlot[this._slot] > 0
    },
    isSmallBox () {
      return this.boxWidth / this.$grid.boxWidth < 20
    },
    isTinyBox () {
      return this.boxWidth / this.$grid.boxWidth < 8
    },
    boxWidth () {
      return this.$parent.$props.w * this.$grid.boxWidth
    },
    boxHeight () {
      return this.$parent.$props.h * this.$grid.boxHeight
    },
    boxX () {
      return this.$parent.$props.x * this.$grid.boxWidth
    },
    boxY () {
      return this.$parent.$props.y * this.$grid.boxHeight
    },
    $localeMode () {
      return this.$parent.$localeMode
    }
  },
  methods: {
    flip () {
      this.$emit('flip')
    },
    togglePropEditor () {
      this.$router.replace({ query: { edit: this.$store.getters.path } })
    },
    toggleChildPropEditor () {
      this.$router.replace({ query: { edit: this.$store.getters.childrenBySlot[this._slot][0].getters.path } })
    },
    toggleMenu () {
      this.showMenu = !this.showMenu
    },
    toggleChildMenu () {
      this.showChildMenu = !this.showChildMenu
    },
    onResizeStart () {
      this.resizing = true
    },
    saveNewDimensions (left, top, width, height) {
      this.resizing = false
      this.save('x', Math.round(left / this.$grid.boxWidth))
      this.save('y', Math.round(top / this.$grid.boxHeight))
      this.save('w', Math.round(width / this.$grid.boxWidth))
      this.save('h', Math.round(height / this.$grid.boxHeight))
    },
    onDragStart () {
      this.dragging = true
    },
    saveNewPosition (left, top) {
      this.dragging = false
      this.save('x', Math.round(left / this.$grid.boxWidth))
      this.save('y', Math.round(top / this.$grid.boxHeight))
    },
    onMouseOver () {
      this.mouseover = true
    },
    onMouseLeave () {
      this.mouseover = false
    },
    save (key, value) {
      this.$store.dispatch('setProp', {
        key,
        value
      })
    },
    deleteBox () {
      const dispatch = () => {
        this.$store.dispatch('remove')
      }
      if (this.hasChildren) {
        new Confirm(this.$t('attention'), this.$t('content will be removed'), dispatch).open()
      } else {
        dispatch()
      }
    },
    setChild (childType) {
      const dispatch = async () => {
        await this.$store.dispatch('setChild', {
          childType,
          slot: this._slot
        })
        this.toggleChildPropEditor()
      }
      if (this.hasActiveChildren) {
        new Confirm(this.$t('attention'), this.$t('content will be removed'), dispatch).open()
      } else {
        dispatch()
      }
      this.showChildMenu = false
    },
    move (movement) {
      this.$store.dispatch('move', {
        movement
      })
    },
    cloneBox () {
      this.$store.dispatch('clone', {
        // TODO: Do something smarter then just x+1, y+1 to "see" the box after cloning
        props: {
          x: this.$parent.$props.x + 1,
          y: this.$parent.$props.y + 1
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@/uiColors';

$hamburger-layer-width: 24px;
$hamburger-layer-height: 3px;
$hamburger-layer-spacing: 4px;
$hamburger-padding-x: 5px;
$hamburger-padding-y: 0;
$hamburger-layer-color: white;
$hamburger-types: (minus);

@import '~hamburgers/_sass/hamburgers/hamburgers';

.active {
  &.box {
    background-color: rgba($color-main-darker, 0.9);
  }
}

.edit-bar {
  pointer-events: auto;
  margin-top: 10px;
  margin-left: 10px;
  height: 32px;
  padding: 4px;
  display: flex;
  align-items: center;
  position: absolute;
  background-color: rgba($color-main-darker, 0.9);
  border-radius: 2px;
  align-content: center;
  cursor: move;
  font-size: 14px;
  line-height: 1.5em;
  letter-spacing: 0.5pt;
  z-index: z(boxEditorEditBar);

  &.transparent {
    background: transparent;
  }

  > svg {
    margin: 0 6px;
  }

  .hamburger {
    margin-left: auto;
    margin-top: 3px;

    &.offset {
      right: -60px;
      position: absolute;
    }
  }

  .menu-dropdown {
    height: fit-content;
    background-color: rgba($color-main-darker, 0.9);
    transform-origin: top;
    transition: transform 250ms ease-in-out;
    position: absolute;
    width: 42px;

    .entry {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 42px;
      background-color: transparent;
      line-height: 0;
      cursor: pointer;

      &:first-child {
        &:hover {
          background: rgba(193, 3, 3, 0.9);
        }
      }

      &:hover {
        background-color: $color-main;
      }
    }
  }

  .child-menu {
    position: absolute;
    top: 32px;
    left: 56px;
    width: auto;
    height: fit-content;
    background-color: rgba($color-main-darker, 0.9);
    transform-origin: top;
    transition: all 250ms ease-in-out;

    &.offset {
      top: 14px;
      left: 42px;
      transform-origin: bottom;
    }

    .child-type {
      height: 32px;
      display: flex;
      align-items: center;
      cursor: pointer;
      border-bottom: 1px solid rgba(#fff, 0.25);
      padding: 0 10px;

      &:last-child {
        border-bottom: none;
      }

      &:hover {
        background-color: $color-main;
      }

      &:active {
        font-size: smaller;
      }

      &.remove-child {
        background: rgba(193, 3, 3, 0.5);

        &:hover {
          background: rgba(193, 3, 3, 0.8);
        }
      }
    }
  }
}
</style>
